import basicForm from "./basicForm";
import paymentForm from "./paymentForm";
import { createBasisTheoryClient } from "./util/basisTheoryClient";
import { btElementStyle } from "./util/basisTheoryStyle";

export default function paymentAchForm (opts = {}) {
    const basisTheoryEnabled = opts.basisTheoryEnabled === true;
    const basisTheorySessionUrl = opts.basisTheorySessionUrl || '';

    return {
        ...basicForm(),
        ...paymentForm(),
        accountType: null,

        basisTheoryEnabled,
        basisTheorySessionUrl,
        btClient: null,
        btRoutingElement: null,
        btAccountElement: null,
        btBankToken: '',
        btTokenizingPromise: null,
        btTokenizationSeq: 0,
        btError: '',
        btRoutingInvalid: false,
        btAccountInvalid: false,
        // BT SDK v2 has no synchronous getState() — mirror change
        // event payload so validate() + submit() can read it.
        btRoutingEmpty: true,
        btAccountEmpty: true,
        btRoutingFocused: false,
        btAccountFocused: false,

        validate () {
            this.isValid = 0 === this.$el.querySelectorAll('[required]:invalid').length;
            if (!this.basisTheoryEnabled) {
                return;
            }
            this.isValid = this.isValid && !this.btRoutingEmpty && !this.btAccountEmpty;
        },

        async init () {
            // paymentForm's init wires the payment-amount
            // sanitizer via $refs.paymentAmount — call it first so we
            // don't regress the legacy path.
            paymentForm().init.call(this);

            if (!this.basisTheoryEnabled) {
                return;
            }

            try {
                this.btClient = await createBasisTheoryClient(this.basisTheorySessionUrl);

                this.btRoutingElement = this.btClient.createElement('text', {
                    targetId: 'bt-routing-number',
                    style: btElementStyle,
                });
                this.btAccountElement = this.btClient.createElement('text', {
                    targetId: 'bt-account-number',
                    style: btElementStyle,
                });

                // Subscribe before mount so any ready/change BT emits
                // during mount is captured.
                this.btRoutingElement.on('focus', () => { this.btRoutingFocused = true; });
                this.btRoutingElement.on('blur', () => { this.btRoutingFocused = false; });
                this.btAccountElement.on('focus', () => { this.btAccountFocused = true; });
                this.btAccountElement.on('blur', () => { this.btAccountFocused = false; });

                this.btRoutingElement.on('change', (e) => {
                    // Any edit invalidates the intent a previous submit
                    // resolved, and supersedes one still in flight —
                    // otherwise submit()'s existing-token guard would
                    // send an id describing the old digits.
                    ++this.btTokenizationSeq;
                    this.btBankToken = '';
                    this.btRoutingEmpty = e.empty;
                    if (!e.empty) this.btRoutingInvalid = false;
                    this.validate();
                });
                this.btAccountElement.on('change', (e) => {
                    // Any edit invalidates the intent a previous submit
                    // resolved, and supersedes one still in flight —
                    // otherwise submit()'s existing-token guard would
                    // send an id describing the old digits.
                    ++this.btTokenizationSeq;
                    this.btBankToken = '';
                    this.btAccountEmpty = e.empty;
                    if (!e.empty) this.btAccountInvalid = false;
                    this.validate();
                });

                await this.btRoutingElement.mount('#bt-routing-number');
                await this.btAccountElement.mount('#bt-account-number');
            } catch (error) {
                this.btError = 'Failed to initialize payment form — please reload the page.';
                console.error('Basis Theory bank init failed', error);
            }
        },

        async submit (event) {
            if (!this.basisTheoryEnabled) {
                return basicForm().submit.call(this);
            }

            if (this.btBankToken) {
                return basicForm().submit.call(this);
            }

            if (!this.btClient || !this.btRoutingElement || !this.btAccountElement) {
                event.preventDefault();
                this.btError = this.btError || 'Payment form not ready — please reload the page.';
                return;
            }

            event.preventDefault();

            // Block empty submits at the client. routing/account have no
            // NotBlank on the BasisTheoryPayment validation group (raw
            // digits never reach the server), so without this guard BT
            // would tokenize empty strings and Equiant would see an
            // empty payload after detokenization.
            if (this.btRoutingEmpty || this.btAccountEmpty) {
                this.btRoutingInvalid = this.btRoutingEmpty;
                this.btAccountInvalid = this.btAccountEmpty;
                this.btError = 'Please enter both routing and account numbers.';
                return;
            }

            const tokenizationSeq = ++this.btTokenizationSeq;

            this.btTokenizingPromise = (async () => {
                try {
                    // ONE intent covering both fields — the facade's
                    // token intent expressions reference this UUID twice
                    // via $.data.routing_number and $.data.account_number.
                    const tokenIntent = await this.btClient.tokenIntents.create({
                        type: 'bank',
                        data: {
                            routing_number: this.btRoutingElement,
                            account_number: this.btAccountElement,
                        },
                    });
                    if (tokenizationSeq !== this.btTokenizationSeq) {
                        return null;
                    }
                    this.btBankToken = tokenIntent.id;
                    await basicForm().submit.call(this);
                    this.$nextTick(() => { this.$refs.form.submit(); });
                    return tokenIntent.id;
                } catch (err) {
                    if (tokenizationSeq !== this.btTokenizationSeq) {
                        return null;
                    }
                    this.btError = 'Failed to tokenize bank account — please re-enter routing + account numbers.';
                    this.btBankToken = '';
                    this.btTokenizingPromise = null;
                    console.error('Basis Theory bank tokenization failed', err);
                }
            })();

            await this.btTokenizingPromise;
        },
    };
}
